import type { PreviewSettingsRequest, ReasonCode, SettingsPreviewResponse } from '../../api';
import type { ApiError } from '../../lib/api/api-client';

import { sameSizing } from './settings-draft';

/**
 * The money example of E7 (M2 R4), for the request it was asked for: the sizing and, since P9,
 * the fallback SL distance (`null` when none applies). `idle` while the size is incomplete;
 * `loading` from the first keystroke, so Save is disabled at once (AC5).
 */
export type RiskPreviewState =
  | { readonly status: 'idle' }
  | ({ readonly status: 'loading' } & PreviewSettingsRequest)
  | ({
      readonly status: 'ready';
      readonly preview: SettingsPreviewResponse;
    } & PreviewSettingsRequest)
  | ({ readonly status: 'failed'; readonly error: ApiError } & PreviewSettingsRequest);

/**
 * True only when the preview on screen was computed for exactly the request of the form: the size
 * and the fallback distance (M2 spec §5 `isPreviewCurrent`, P9 R6). Save needs it: the trader
 * always saw what one trade can cost (FXM-02 §7), with or without a stop.
 */
export function isPreviewCurrent(
  request: PreviewSettingsRequest | null,
  preview: RiskPreviewState,
): boolean {
  return (
    request !== null &&
    preview.status === 'ready' &&
    sameSizing(request.sizing, preview.sizing) &&
    request.fallbackStopLossPips === preview.fallbackStopLossPips
  );
}

/** What E7 shows for a failed preview (M2 §4, AC5). */
export interface PreviewFailure {
  /** The C2 text to show. */
  readonly reasonCode: Extract<
    ReasonCode,
    'TRADING_ACCOUNT_NOT_CONNECTED' | 'NOT_FOUND' | 'VALIDATION_FAILED' | 'RISK_PREVIEW_UNAVAILABLE'
  >;
  /** Retry only when asking again can give another answer (account unreachable, server error). */
  readonly retry: boolean;
  /** The API refused the size itself: the size field is marked (M2 R20). */
  readonly marksSize: boolean;
}

/**
 * Maps a preview failure to what E7 shows. A refusal that says why keeps its own C2 text and no
 * Retry, since asking again gives the same answer: no account connected (409, C1), an unknown
 * subscription (404), a size the API refuses (400, the field is marked). Anything else means the
 * risk cannot be computed now: `RISK_PREVIEW_UNAVAILABLE` with a Retry (M2 §4).
 */
export function previewFailure(error: ApiError): PreviewFailure {
  switch (error.reasonCode) {
    case 'TRADING_ACCOUNT_NOT_CONNECTED':
    case 'NOT_FOUND':
      return { reasonCode: error.reasonCode, retry: false, marksSize: false };
    case 'VALIDATION_FAILED':
      return { reasonCode: 'VALIDATION_FAILED', retry: false, marksSize: true };
    default:
      return { reasonCode: 'RISK_PREVIEW_UNAVAILABLE', retry: true, marksSize: false };
  }
}
